import { APIEntity, dataManager } from '../../../APIEntity';
import { ApiError } from '../../../ApiResponse';
import { TypeId } from '../../../models/TypeId';
import { useCallback, useRef, useSyncExternalStore } from 'react';
import { useEntityOptions, UseEntityResult } from './types';
import { useWatch } from './useWatch';

type EntityQueryState<T> = {
  data: T | null | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: ApiError | null;
  isError: boolean;
  isSuccess: boolean;
  notFound: boolean;
};

/** The state a target starts in. A target the cache already holds starts READY with it: the
 *  subscribe below runs only after commit, so starting `undefined` here rendered every cached
 *  target as missing for one commit — a list row whose latest-message pointer moved to a known
 *  message vanished and came back. */
function initialState<T>(typeId: TypeId | null, enabled: boolean): EntityQueryState<T> {
  const cached = typeId && enabled ? (dataManager.getByTypeIdFromCache(typeId) as T | null) : null;
  if (cached) {
    return { data: cached, isLoading: false, isFetching: false, error: null, isError: false, isSuccess: true, notFound: false };
  }
  return {
    data: typeId && enabled ? undefined : null,
    isLoading: !!(typeId && enabled),
    isFetching: false,
    error: null,
    isError: false,
    isSuccess: false,
    notFound: false,
  };
}

export function useEntity<T extends APIEntity<T>>(
  typeId: TypeId | null,
  options?: useEntityOptions<T>,
): UseEntityResult<T> {
  const { watch, query, enabled = true } = options || {};
  const queryJsonStringified = JSON.stringify(query);

  // State refs to track query state
  const stateRef = useRef<EntityQueryState<T>>(initialState(typeId, enabled));

  // Cache the snapshot to prevent unnecessary re-renders
  const snapshotRef = useRef(stateRef.current);

  // A TypeId/query switch must never expose the previous entity for even one
  // render. Consumers commonly key dependent queries from the new TypeId; a
  // stale entity here can otherwise populate that new cache key with bytes from
  // the old target (for example, rapid Wiki word navigation).
  const inputKey = `${enabled}:${typeId?.type ?? ''}:${typeId?.id ?? ''}:${queryJsonStringified}`;
  const inputKeyRef = useRef(inputKey);
  if (inputKeyRef.current !== inputKey) {
    inputKeyRef.current = inputKey;
    stateRef.current = initialState(typeId, enabled);
    snapshotRef.current = stateRef.current;
  }

  // Ref to the useSyncExternalStore notify callback so refetch() can trigger re-renders
  const notifyRef = useRef<(() => void) | null>(null);

  // Subscribe function for useSyncExternalStore
  const subscribe = useCallback(
    (callback: () => void) => {
      notifyRef.current = callback;
      if (!typeId || !enabled) {
        // When disabled or no typeId, immediately set to non-loading null state
        stateRef.current = {
          data: null,
          isLoading: false,
          isFetching: false,
          error: null,
          isError: false,
          isSuccess: false,
          notFound: false,
        };
        callback();
        return () => {};
      }

      // Set loading state immediately when subscribing — unless the cache already answers
      // (`initialState`), which `initialize` confirms without a fetch.
      if (stateRef.current.data === undefined) {
        stateRef.current = {
          data: undefined,
          isLoading: true,
          isFetching: true,
          error: null,
          isError: false,
          isSuccess: false,
          notFound: false,
        };
        callback(); // Trigger re-render immediately with loading state
      }

      let unsubscribe: (() => void) | undefined;
      let cancelled = false;

      // Async initialization
      const initialize = async () => {
        try {
          // Check cache first to avoid unnecessary API calls
          const cachedEntity = dataManager.getByTypeIdFromCache<T>(typeId);
          if (cachedEntity) {
            if (cancelled) return;
            stateRef.current = {
              data: cachedEntity,
              isLoading: false,
              isFetching: false,
              error: null,
              isError: false,
              isSuccess: true,
              notFound: false,
            };
            callback();
          } else {
            // Only fetch from API if not in cache
            const entity = await dataManager.getByTypeId<T>(typeId, query);
            if (cancelled) return;
            // A null result with the ref flagged not-found is a terminal 404 —
            // the entity has no local row. Surface it as ``notFound`` (not an
            // error) so consumers can render an "unavailable" state, and so the
            // store's negative cache keeps us from re-fetching on every render.
            const notFound = entity === null && dataManager.isNotFound(typeId);
            stateRef.current = {
              data: entity,
              isLoading: false,
              isFetching: false,
              error: null,
              isError: false,
              isSuccess: true,
              notFound,
            };
            callback();
          }

          // Subscribe to updates from data manager
          unsubscribe = dataManager.subscribe<T>(typeId, (updatedEntity) => {
            if (cancelled) return;
            stateRef.current = {
              data: updatedEntity,
              isLoading: false,
              isFetching: false,
              error: null,
              isError: false,
              isSuccess: true,
              notFound: false,
            };
            // Force snapshot update since entity object reference doesn't change
            snapshotRef.current = { ...stateRef.current };
            callback(); // Trigger re-render with updated data
          });
        } catch (err) {
          if (cancelled) return;
          console.error('useEntity.ts: Error fetching entity by type ID:', typeId, err);
          stateRef.current = {
            data: null,
            isLoading: false,
            isFetching: false,
            error: err as ApiError,
            isError: true,
            isSuccess: false,
            notFound: false,
          };
          callback(); // Trigger re-render to show error
        }
      };

      void initialize();

      return () => {
        cancelled = true;
        if (unsubscribe) {
          unsubscribe();
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, typeId?.type, typeId?.id, queryJsonStringified],
  );

  // Snapshot function for useSyncExternalStore
  const getSnapshot = useCallback(() => {
    const current = stateRef.current;

    // Only create new snapshot if something changed
    if (
      snapshotRef.current.data !== current.data ||
      snapshotRef.current.isLoading !== current.isLoading ||
      snapshotRef.current.isFetching !== current.isFetching ||
      snapshotRef.current.error !== current.error ||
      snapshotRef.current.isError !== current.isError ||
      snapshotRef.current.isSuccess !== current.isSuccess ||
      snapshotRef.current.notFound !== current.notFound
    ) {
      snapshotRef.current = { ...current };
    }

    return snapshotRef.current;
  }, []);

  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // Refetch function for manual data refresh
  const refetch = useCallback(async () => {
    if (!typeId || !enabled) return;

    stateRef.current = { ...stateRef.current, isFetching: true, error: null, isError: false };

    // A manual refetch is an explicit retry — drop any terminal 404 marker so
    // the store actually re-hits the network instead of short-circuiting.
    if (dataManager.isNotFound(typeId)) {
      dataManager.invalidateCacheByTypeId(typeId);
    }

    try {
      // A refetch reads the backend, never the cache: the caller asks because it learned
      // the entity changed (a `projected` tag, a reload button) — and a change that reached
      // it without an entity op left the cached entry stale, which the cache-first
      // `getByTypeId` would hand straight back.
      // `fetchByTypeId` already loads when `query.load` asks for it.
      const entity = (await dataManager.refreshByTypeId(typeId, query)) as T | null;
      const notFound = entity === null && dataManager.isNotFound(typeId);
      stateRef.current = {
        data: entity,
        isLoading: false,
        isFetching: false,
        error: null,
        isError: false,
        isSuccess: true,
        notFound,
      };
    } catch (err) {
      stateRef.current = {
        data: null,
        isLoading: false,
        isFetching: false,
        error: err as ApiError,
        isError: true,
        isSuccess: false,
        notFound: false,
      };
    }
    snapshotRef.current = { ...stateRef.current };
    notifyRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, typeId?.type, typeId?.id, queryJsonStringified]);

  // Set up watch functionality - only watch if entity is saved
  const entity = state.data;
  const shouldWatch = (watch ?? false) && (entity?.saved ?? false);
  useWatch(typeId, shouldWatch);

  return {
    ...state,
    refetch,
  };
}
